import React, { useState } from 'react';
import { DollarSign, Shield, Users, BarChart3, Mail, Lock, ArrowLeft } from 'lucide-react';
import { useAuth } from './AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const { login, resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.endsWith('@labanquita.com')) {
      setError('Solo usuarios @labanquita.com pueden acceder al sistema');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login(email, password);
    } catch (err) {
      console.error('Auth error:', err);
      
      let errorMessage = err.message;
      
      if (err.message.includes('Invalid login credentials')) {
        errorMessage = 'Credenciales incorrectas. Verifica tu email y contraseña.';
      } else if (err.message.includes('Email not confirmed')) {
        errorMessage = 'Por favor confirma tu email antes de iniciar sesión.';
      } else if (err.message.includes('too_many_requests')) {
        errorMessage = 'Demasiados intentos. Espera unos minutos e intenta nuevamente.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Por favor ingresa tu email');
      return;
    }

    if (!email.endsWith('@labanquita.com')) {
      setError('Solo usuarios @labanquita.com pueden restablecer contraseña');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await resetPassword(email);
      setResetSuccess(true);
      setLoading(false);
    } catch (err) {
      console.error('Reset error:', err);
      setError('Error al enviar email de recuperación. Intenta nuevamente.');
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetSuccess(false);
    setError('');
    setEmail('');
    setPassword('');
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gray-800 dark:bg-black flex items-center justify-center px-4 relative overflow-hidden">
        {/* Animated background effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-gradient-radial from-teal-500/20 to-transparent blur-3xl -top-48 -left-48 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent blur-3xl -bottom-48 -right-48 animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="max-w-md w-full relative z-10">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <DollarSign className="h-12 w-12 text-teal-300 mr-3" />
              <h1 className="text-4xl font-bold text-teal-300">La Banquita</h1>
            </div>
            <p className="text-lg text-gray-300">Recuperar Contraseña</p>
          </div>

          {/* Reset Password Card */}
          <div className="card-neon rounded-xl p-8">
            <button
              onClick={handleBackToLogin}
              className="flex items-center text-teal-300 hover:text-teal-200 mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al inicio de sesión
            </button>

            {resetSuccess ? (
              <div className="text-center py-8">
                <div className="inline-block p-4 rounded-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 mb-4">
                  <Mail className="h-12 w-12 text-teal-300" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">¡Email Enviado!</h2>
                <p className="text-gray-400 mb-6">
                  Hemos enviado un enlace de recuperación a:
                </p>
                <p className="text-teal-300 font-semibold mb-6">{email}</p>
                <p className="text-sm text-gray-500">
                  Revisa tu bandeja de entrada y sigue las instrucciones para restablecer tu contraseña.
                </p>
                <button
                  onClick={handleBackToLogin}
                  className="mt-6 btn-neon text-black font-bold py-2 px-6 rounded-lg"
                >
                  Volver al Inicio
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="inline-block p-4 rounded-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 mb-4">
                    <Lock className="h-12 w-12 text-teal-300" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">¿Olvidaste tu contraseña?</h2>
                  <p className="text-gray-400">
                    Ingresa tu email y te enviaremos un enlace para restablecerla
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
                    <p className="text-red-400 text-sm text-center">{error}</p>
                  </div>
                )}

                {/* Reset Form */}
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-teal-300" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu-email@labanquita.com"
                        required
                        disabled={loading}
                        className="w-full pl-10 pr-3 py-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400 disabled:opacity-50 transition-all"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-neon text-black font-bold py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin-glow rounded-full h-5 w-5 border-2 border-black border-t-transparent mr-2"></div>
                        Enviando...
                      </div>
                    ) : (
                      'Enviar Enlace de Recuperación'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main Login Form
  return (
    <div className="min-h-screen bg-gray-800 dark:bg-black flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated background effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-gradient-radial from-teal-500/20 to-transparent blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent blur-3xl -bottom-48 -right-48 animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <DollarSign className="h-12 w-12 text-teal-300 mr-3" />
            <h1 className="text-4xl font-bold text-teal-300">La Banquita</h1>
          </div>
          <p className="text-lg text-gray-300">Sistema de Gestión de Préstamos</p>
          <p className="text-sm text-cyan-400 mt-2">Autenticación Segura</p>
        </div>

        {/* Login Card */}
        <div className="card-neon rounded-xl p-8">
          <div className="text-center mb-6">
            <div className="inline-block p-4 rounded-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 mb-4 glow-mint-sm">
              <Shield className="h-12 w-12 text-teal-300" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Acceso Seguro</h2>
            <p className="text-gray-400">Iniciar sesión en tu cuenta</p>
            <p className="text-sm text-teal-300 mt-1">Solo usuarios @labanquita.com</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg glow-mint-sm">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-teal-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu-email@labanquita.com"
                  required
                  disabled={loading}
                  className="w-full pl-10 pr-3 py-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400 disabled:opacity-50 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-teal-300" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  disabled={loading}
                  minLength={6}
                  className="w-full pl-10 pr-3 py-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400 disabled:opacity-50 transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-neon text-black font-bold py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin-glow rounded-full h-5 w-5 border-2 border-black border-t-transparent mr-2"></div>
                  Iniciando sesión...
                </div>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="text-center">
            <button
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-teal-300 hover:text-teal-200 transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          {/* Features Preview */}
          <div className="border-t border-teal-500/20 pt-6 mt-6">
            <h3 className="text-sm font-semibold text-gray-300 mb-3 text-center">
              Características del Sistema
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm text-gray-400">
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-2 text-teal-300" />
                <span>Gestión de Clientes</span>
              </div>
              <div className="flex items-center">
                <BarChart3 className="h-4 w-4 mr-2 text-cyan-400" />
                <span>Métricas Avanzadas</span>
              </div>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 mr-2 text-teal-300" />
                <span>Control de Pagos</span>
              </div>
              <div className="flex items-center">
                <Shield className="h-4 w-4 mr-2 text-cyan-400" />
                <span>Acceso Seguro</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            © 2025 La Banquita. Sistema protegido con autenticación empresarial.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;